/**
 * Prix et devise d'une proposition — règles communes aux quatre écrans qui en envoient
 * (ProposePage, ProposeModal, ShipmentMatchingPage, TripMatchingPage). Hook associé : usePropositionDraft.
 *
 * Vocabulaire : `meSide` ('sender' | 'carrier') · `target` l'annonce visée · `mine` mon annonce ·
 * `mode` ('direct' | 'negotiation').
 *
 * - Proposition directe : le prix de l'annonce visée (`targetPrice`), verrouillé — j'accepte le prix de l'autre.
 * - Négociation : MON prix (`ownPrice`), dans la devise de MON annonce ; le prix de l'annonce visée reste
 *   le repère auquel la contre-offre est comparée (`negotiationDiff`).
 * - Un prix n'est jamais lu dans une autre devise que celle de l'annonce dont il vient.
 */
import { normalizeCurrencies, getCommonCurrencies, ccyStripeOk } from '@/utils/currencyUtils';
import { ZERO_DEC_CCY } from '@/utils/tripPriceUtils';

/**
 * Montant à la précision de sa devise : au centime, ou à l'unité pour les devises sans décimales.
 * Un prix n'est jamais arrondi à l'unité dans une devise à décimales : 2,5 EUR × 3 kg = 7,50 EUR, pas 8 (D25).
 */
export function toCurrencyPrecision(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return ZERO_DEC_CCY.has(currency) ? Math.round(n) : Math.round(n * 100) / 100;
}

/** Un prix à l'écran : « 9 » ou « 7,50 » — les décimales seulement quand il y en a. */
export function priceLabel(amount) {
  const n = Number(amount);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Mode de tarif d'un trajet : 'kg' | 'fixe' | 'libre' ; au kilo s'il n'est pas précisé. */
export function tripPricingMode(trip) {
  return trip?.pricing_mode || 'kg';
}

/** Prix d'un trajet pour un colis donné : au kilo × poids du colis, ou le forfait ; null au tarif libre. */
function tripPrice(trip, shipment) {
  const mode = tripPricingMode(trip);
  if (mode === 'libre') return null;
  return mode === 'fixe' ? trip.price_fixed : Number(trip.price_per_kg) * Number(shipment?.weight_kg);
}

function priced(amount, listing) {
  const currency = listing.primary_currency || 'EUR';
  const exact = toCurrencyPrecision(amount, currency);
  return exact > 0 ? { amount: exact, currency } : null;
}

/**
 * Prix de l'annonce visée, dans sa devise : tarif du trajet visé (sender) ou budget du colis visé (carrier).
 * Retourne { amount, currency } ou null (tarif libre, colis sans budget, ou mon colis pas encore choisi).
 */
export function targetPrice(meSide, target, mine) {
  if (!target) return null;
  return priced(meSide === 'sender' ? tripPrice(target, mine) : target.price_offered, target);
}

/**
 * Mon prix, dans la devise de mon annonce : budget de mon colis (sender) ou tarif de mon trajet (carrier).
 * Retourne { amount, currency } ou null (colis sans budget, trajet au tarif libre, rien de choisi).
 */
export function ownPrice(meSide, mine, target) {
  if (!mine) return null;
  return priced(meSide === 'sender' ? mine.price_offered : tripPrice(mine, target), mine);
}

/** L'annonce visée n'a aucun prix à accepter tel quel : négociation seulement. */
export function hasNoPriceToAccept(meSide, target) {
  if (!target) return false;
  return meSide === 'sender' ? tripPricingMode(target) === 'libre' : !target.price_offered;
}

/**
 * L'annonce visée accepte-t-elle une contre-offre ? Toujours quand elle n'a rien à accepter (tarif libre, colis sans
 * budget) : c'est la seule façon de proposer. Sinon `counter_offers` fait foi (« prix ferme » d'un trajet, « budget
 * ferme » d'un colis) ; ouvert s'il n'est pas précisé.
 */
export function isNegotiable(meSide, target) {
  if (!target || hasNoPriceToAccept(meSide, target)) return true;
  return target.counter_offers !== false;
}

/**
 * Devise présélectionnée : la première devise commune dans l'ordre de l'annonce visée (sa devise principale
 * quand elle est commune), sinon la première devise de l'annonce visée. Seules les devises payables par carte
 * sont retenues : le cedi affiche un prix, il ne conclut pas (D9).
 */
export function defaultCurrency(target, mine) {
  const theirs = normalizeCurrencies(target);
  const ours = mine ? normalizeCurrencies(mine) : [];
  const common = getCommonCurrencies(theirs, ours);
  return [...common, ...theirs, ...ours].find(ccyStripeOk) || 'EUR';
}

/**
 * Prix de la contre-offre dans la devise choisie.
 *
 * `converted` : montant converti dans la devise choisie, ou null quand il n'y a pas de conversion —
 * même devise que le prix de départ (il reste valable) ou taux indisponible (on vide le champ).
 */
export function negotiationPrice(converted, propCurrency, baseCurrency, suggested) {
  if (converted != null) return String(toCurrencyPrecision(converted, propCurrency));
  return propCurrency === baseCurrency && suggested ? String(suggested) : '';
}

/**
 * Écart entre la contre-offre et le prix de l'annonce visée, tous deux dans la devise choisie.
 * `refKind` : 'tarif' (trajet visé) ou 'budget' (colis visé). Retourne { cls, label } ou null.
 */
export function negotiationDiff(price, ref, refKind, sym) {
  if (!price || ref == null) return null;
  const diff = Number(price) - ref;
  const cents = (x) => priceLabel(Math.round(x * 100) / 100);
  const refLabel = `${cents(ref)} ${sym}`;
  if (diff < -0.01) return { cls: 'dp-diff--under', label: `${cents(diff)} ${sym} sous le ${refKind} (réf. ${refLabel})` };
  if (diff > 0.01) return { cls: 'dp-diff--over', label: `+${cents(diff)} ${sym} au-dessus du ${refKind} (réf. ${refLabel})` };
  return { cls: 'dp-diff--eq', label: `Identique au ${refKind} (réf. ${refLabel})` };
}
